import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserCog } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../packages/auth-client';
import { apiClient, getErrorMessage } from '../../packages/api-client';
import type { PaginatedResponse, User } from '../../packages/api-client/pm-api';
import { Dialog, PageHeader, StateView } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';

interface Tenant {
    id: string;
    name: string;
    deleted_at?: string | null;
}

// The API's upper page size; a larger tenant is narrowed with the search box.
const USER_LIMIT = 100;
const REASON_MAX = 500;

const displayName = (u: User) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email;

/**
 * Platform impersonation: pick a tenant, then one of its users, then confirm
 * with a reason. The session is a 30-minute token carrying the target's
 * tenant, roles and restrictions; start and exit are audit-logged with the
 * operator as actor, and writes carry auth_impersonated_by.
 */
export function ImpersonatePage() {
    usePageTitle('Impersonate');
    const { startImpersonation, isSuperAdmin } = useAuth();
    const superAdmin = isSuperAdmin();
    const [tenantId, setTenantId] = useState('');
    const [search, setSearch] = useState('');
    const [target, setTarget] = useState<User | null>(null);
    const [reason, setReason] = useState('');
    const [starting, setStarting] = useState(false);

    const tenantsQ = useQuery({
        queryKey: ['platform', 'tenants'],
        queryFn: async () => (await apiClient.get<{ data: Tenant[] }>('/tenants')).data.data ?? [],
        enabled: superAdmin,
    });
    const usersQ = useQuery({
        queryKey: ['platform', 'users', tenantId, search],
        queryFn: async () => (await apiClient.get<PaginatedResponse<User>>('/users', {
            params: { tenant_id: tenantId, search, limit: USER_LIMIT },
        })).data,
        enabled: superAdmin && tenantId !== '',
    });

    if (!superAdmin) {
        return <StateView state="denied" reason="Superadmin access is required to impersonate users."
            nextStep="Sign in with the platform operator account." />;
    }

    const tenants = (tenantsQ.data ?? []).filter(t => !t.deleted_at);
    const tenantName = tenants.find(t => t.id === tenantId)?.name ?? tenantId;
    const users = usersQ.data?.data ?? [];
    const total = usersQ.data?.meta?.total ?? users.length;

    const close = () => { if (!starting) { setTarget(null); setReason(''); } };

    const confirm = async () => {
        if (!target) return;
        setStarting(true);
        try {
            await startImpersonation(target.id, { reason: reason.trim(), tenantName });
            // Full load: every provider and cache starts from the new identity.
            window.location.href = '/dashboard';
        } catch (err) {
            toast.error(getErrorMessage(err));
            setStarting(false);
        }
    };

    let list: React.ReactNode;
    if (!tenantId) {
        list = <p style={{ color: 'var(--ink-muted)' }}>Choose a tenant to list its users.</p>;
    } else if (usersQ.isPending) {
        list = <StateView state="loading" label="Loading users" />;
    } else if (usersQ.isError) {
        list = <StateView state="error" message={getErrorMessage(usersQ.error)} onRetry={() => usersQ.refetch()} />;
    } else if (users.length === 0) {
        list = <StateView state="empty" title={search ? 'No users match this search' : 'This tenant has no users'} />;
    } else {
        list = (
            <>
                <p style={{ color: 'var(--ink-muted)', fontSize: '0.8rem', margin: 0 }}>
                    {total > users.length
                        ? `Showing ${users.length} of ${total} users. Search to narrow the list.`
                        : `${users.length} user${users.length === 1 ? '' : 's'}`}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {users.map(u => (
                        <li key={u.id} className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem 1rem' }}>
                            <div>
                                <div style={{ fontWeight: 700 }}>{displayName(u)}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>
                                    {u.email}
                                    {!u.is_active && <span style={{ color: 'var(--danger)' }}> · Inactive</span>}
                                    {u.is_superadmin && <span> · Superadmin, cannot be impersonated</span>}
                                </div>
                            </div>
                            <button type="button" className="btn btn-primary btn-sm"
                                aria-label={`Impersonate ${u.email}`}
                                disabled={u.is_superadmin === true}
                                onClick={() => setTarget(u)}>
                                <UserCog size={15} aria-hidden="true" /> Impersonate…
                            </button>
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    return (
        <div style={{ maxWidth: 900 }}>
            <PageHeader title="Impersonate a user"
                subtitle="Act as a tenant user to reproduce what they see. Their restrictions apply, and start and exit are audit-logged." />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
                <div className="form-group" style={{ flex: '1 1 220px' }}>
                    <label htmlFor="imp-tenant" className="form-label">Tenant</label>
                    <select id="imp-tenant" className="form-input" value={tenantId}
                        disabled={tenantsQ.isPending}
                        onChange={e => { setTenantId(e.target.value); setSearch(''); }}>
                        <option value="">{tenantsQ.isPending ? 'Loading tenants…' : 'Select a tenant'}</option>
                        {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div className="form-group" style={{ flex: '2 1 280px' }}>
                    <label htmlFor="imp-search" className="form-label">Search users</label>
                    <input id="imp-search" type="search" className="form-input" placeholder="Name or email"
                        value={search} disabled={!tenantId} onChange={e => setSearch(e.target.value)} />
                </div>
            </div>
            {tenantsQ.isError && (
                <StateView state="error" message={getErrorMessage(tenantsQ.error)} onRetry={() => tenantsQ.refetch()} />
            )}

            {list}

            <Dialog open={target !== null} onClose={close} closeOnBackdrop={false}
                title={target ? `Impersonate ${target.email}?` : ''}
                footer={<>
                    <button type="button" className="btn btn-secondary btn-md" disabled={starting} onClick={close}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-md"
                        disabled={starting || reason.trim() === ''} onClick={confirm}>
                        {starting ? 'Starting…' : 'Start impersonation'}
                    </button>
                </>}>
                {target && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <p style={{ margin: 0 }}>
                            You will act as <strong>{displayName(target)}</strong> ({target.email}) in <strong>{tenantName}</strong>,
                            with their roles and restrictions. Your superadmin access is set aside until you exit.
                        </p>
                        <p style={{ margin: 0 }}>
                            Anything you create or change is recorded as this user and attributed to you as the impersonator.
                            The start and the exit are written to the audit log with this reason.
                        </p>
                        <p style={{ margin: 0 }}>
                            The session lasts up to 30 minutes and is not renewed: when it ends you are signed out and sign in again.
                        </p>
                        <div className="form-group">
                            <label htmlFor="imp-reason" className="form-label">Reason (kept in the audit log)</label>
                            <textarea id="imp-reason" className="form-input" rows={3} maxLength={REASON_MAX} required
                                value={reason} onChange={e => setReason(e.target.value)} />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
}
